/**
 * Badge — small tonal label used for roles, statuses and other neutral-but-
 * categorised text. Tones mirror the tonal vocabulary used elsewhere in the
 * UI (info/warn/good/neutral). For severity-coded alerts use <SevBadge>.
 */
import type { ReactNode } from 'react';

import { cn } from './cn';

export type BadgeTone = 'info' | 'warn' | 'good' | 'neutral' | 'error';

export interface BadgeProps {
  tone?: BadgeTone;
  children: ReactNode;
  className?: string;
}

const TONE_CLASS: Record<BadgeTone, string> = {
  good: 'bg-pass-50 text-pass-700 ring-1 ring-inset ring-pass-100',
  info: 'bg-info-50 text-info-700 ring-1 ring-inset ring-info-100',
  warn: 'bg-warn-50 text-warn-700 ring-1 ring-inset ring-warn-100',
  error: 'bg-fail-50 text-fail-700 ring-1 ring-inset ring-fail-100',
  neutral: 'bg-slate-100 text-slate-700 ring-1 ring-inset ring-slate-200',
};

export function Badge({ tone = 'neutral', children, className }: BadgeProps): JSX.Element {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded px-2 py-0.5 text-xs font-medium',
        TONE_CLASS[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}
